import { memo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ExternalLink, Sparkles, Newspaper } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  formatDate,
  PAGE_SIZE,
  FALLBACK_IMG,
  slugifyMedia,
} from "@/lib/newsUtils";
import { trackOpen } from "@/lib/engagement";

export const NewsCard = memo(function NewsCard({ article, index, hideMediaLink = false }) {
  const mediaSlug = slugifyMedia(article.source);
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.article
      data-testid={`news-card-${index}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: (index % PAGE_SIZE) * 0.04 }}
      style={{ contentVisibility: "auto", containIntrinsicSize: "0 480px" }}
      className="group flex flex-col h-full border border-border bg-card transition-colors duration-200 hover:border-primary"
    >
      <div className="relative h-48 overflow-hidden bg-muted border-b border-border">
        <img
          src={article.image}
          alt={article.title}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = FALLBACK_IMG;
          }}
        />
      </div>
      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              data-testid={`source-badge-${index}`}
              className="rounded-none bg-foreground text-background hover:bg-foreground font-medium uppercase tracking-wide text-[11px]"
            >
              {article.source}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium whitespace-nowrap">
            {formatDate(article.published)}
          </span>
        </div>

        <h2 className="font-heading text-xl font-bold leading-snug tracking-tight text-foreground mb-4">
          {article.title}
        </h2>

        <div className="pl-4 border-l-2 border-primary mb-6">
          <div className="flex items-center gap-1.5 mb-2 text-primary">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-[11px] uppercase tracking-widest font-semibold">
              Resumen Gemini
            </span>
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`summary-toggle-${index}`}
            aria-expanded={expanded}
            className="text-left w-full focus:outline-none group/summary"
          >
            <p
              className={`text-sm leading-relaxed text-foreground/80 ${expanded ? "" : "line-clamp-2"}`}
            >
              {article.summary || "Resumen no disponible."}
            </p>
            <span className="mt-1.5 inline-block text-[11px] uppercase tracking-wide font-semibold text-primary group-hover/summary:underline">
              {expanded ? "Ver menos" : "Leer resumen completo"}
            </span>
          </button>
        </div>

        <div className="mt-auto">
          {hideMediaLink ? (
            <a
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`read-original-button-${index}`}
              onClick={() => trackOpen(article.source)}
              className="inline-flex items-center justify-center gap-2 w-full h-11 bg-primary text-primary-foreground text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Leer noticia original
              <ExternalLink className="w-4 h-4" />
            </a>
          ) : (
            <Link
              to={`/medios/${mediaSlug}`}
              data-testid={`more-from-media-button-${index}`}
              onClick={() => trackOpen(article.source)}
              className="inline-flex items-center justify-center gap-2 w-full h-11 bg-primary text-primary-foreground text-sm font-semibold uppercase tracking-wide transition-colors hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <Newspaper className="w-4 h-4" />
              Ver más del medio
            </Link>
          )}
        </div>
      </div>
    </motion.article>
  );
});

export const SkeletonCard = () => (
  <div className="flex flex-col h-full border border-border bg-card animate-pulse">
    <div className="h-48 bg-muted border-b border-border" />
    <div className="p-6 space-y-4">
      <div className="flex justify-between">
        <div className="h-5 w-24 bg-muted" />
        <div className="h-4 w-16 bg-muted" />
      </div>
      <div className="h-6 w-full bg-muted" />
      <div className="h-6 w-3/4 bg-muted" />
      <div className="h-16 w-full bg-muted" />
      <div className="h-11 w-full bg-muted" />
    </div>
  </div>
);
